import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { ToastProvider } from '@/components/ui';
import { AuthProvider, PermissionProvider } from '@/features/auth';
import { RealtimeProvider } from '@/features/realtime/RealtimeProvider';
import { ApiError } from '@/types/api';

/**
 * Single place where application-wide providers are composed.
 * Order matters: routing -> data -> toasts -> session -> permissions ->
 * realtime. The socket goes last because it needs both a signed-in user and
 * the query client: all it does is turn a frame into an invalidation.
 */
export function AppProviders({ children }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              // Never retry a request the server refused on purpose.
              if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
                return false;
              }
              return failureCount < 2;
            },
          },
        },
      }),
  );

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <PermissionProvider>
              <RealtimeProvider>{children}</RealtimeProvider>
            </PermissionProvider>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
